import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback, useMemo } from 'react'
import useSWR, { type MutatorOptions, useSWRConfig } from 'swr'

type ServerPrefs = {
  channels?: { inapp?: boolean; email?: boolean }
  by_type?: Record<string, unknown>
}

export type NotificationPreferences = {
  channels: { inapp: boolean; email: boolean }
  by_type: Record<string, unknown>
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  channels: { inapp: true, email: true },
  by_type: {},
}

export function useNotificationPreferences(opts?: {
  baseUrl?: string
  userId?: string
  enabled?: boolean
}) {
  const { ALERTS_URL } = useRuntimeVariables()
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { user } = useGeckoPermissions()
  const { mutate: globalMutate } = useSWRConfig()

  const {
    baseUrl = `${ALERTS_URL}/api/v1/notifications`,
    userId: providedUserId,
    enabled = true,
  } = opts ?? {}

  const userId = providedUserId ?? user?.id

  const canFetch = Boolean(enabled && userId && isAuthenticated)

  const preferencesKey = useMemo(
    () => (canFetch ? `preferences:${userId}` : null),
    [canFetch, userId]
  )

  const fetchPreferences =
    useCallback(async (): Promise<NotificationPreferences> => {
      if (!canFetch || !userId) {
        return DEFAULT_PREFERENCES
      }
      try {
        const res = await axios.get<NotificationPreferences>(
          `${baseUrl}/preferences/${encodeURIComponent(userId)}`,
          {
            headers: {
              Accept: 'application/json',
            },
          }
        )
        const data = res.data
        return {
          channels: {
            inapp: data.channels?.inapp ?? true,
            email: data.channels?.email ?? true,
          },
          by_type: data.by_type ?? {},
        }
      } catch {
        return DEFAULT_PREFERENCES
      }
    }, [canFetch, baseUrl, userId, axios])

  const {
    data: preferences,
    error,
    isLoading,
    mutate,
  } = useSWR<NotificationPreferences, Error>(preferencesKey, fetchPreferences, {
    revalidateOnFocus: false,
  })

  const hasLoaded = preferences !== undefined && !isLoading

  const updatePreferences = useCallback(
    async (next: ServerPrefs | { inapp: boolean; email: boolean }) => {
      if (!canFetch || !userId) {
        throw new Error('Cannot update: missing userId or disabled')
      }

      const currentPrefs = preferences ?? DEFAULT_PREFERENCES
      const isUiFormat = 'inapp' in next && 'email' in next
      const nextInapp = isUiFormat
        ? next.inapp
        : (next as ServerPrefs).channels?.inapp
      const nextEmail = isUiFormat
        ? next.email
        : (next as ServerPrefs).channels?.email

      const body: NotificationPreferences = {
        channels: {
          inapp: nextInapp ?? currentPrefs.channels.inapp,
          email: nextEmail ?? currentPrefs.channels.email,
        },
        by_type: currentPrefs.by_type,
      }

      const res = await axios.put<NotificationPreferences>(
        `${baseUrl}/preferences/${encodeURIComponent(userId)}`,
        body,
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      )
      const saved = res.data
      const normalized: NotificationPreferences = {
        channels: {
          inapp: saved.channels?.inapp ?? true,
          email: saved.channels?.email ?? true,
        },
        by_type: saved.by_type ?? {},
      }
      const mutateOptions: MutatorOptions = { revalidate: false }
      mutate(normalized, mutateOptions)
      if (preferencesKey) {
        globalMutate(preferencesKey, normalized, mutateOptions)
      }
      globalMutate(
        (key) =>
          typeof key === 'string' && key.startsWith(`preferences:${userId}`),
        normalized,
        mutateOptions
      )
      await mutate()

      return normalized
    },
    [
      canFetch,
      preferences,
      baseUrl,
      userId,
      axios,
      mutate,
      globalMutate,
      preferencesKey,
    ]
  )

  return {
    preferences: preferences ?? DEFAULT_PREFERENCES,
    isLoading,
    hasLoaded,
    error,
    reload: () => mutate(),
    updatePreferences,
  }
}
